import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useFlutterBridge } from './hooks/useFlutterBridge';
import Splash from './components/common/Splash';
import ProtectedRoute from './components/common/ProtectedRoute';
import Login from './pages/user/Login';
import PhoneInput from './pages/signup/PhoneInput';
import PhoneVerification from './pages/signup/PhoneVerification';
import Verification from './pages/signup/Verification';
import Carrier from './pages/signup/Carrier';
import Gender from './pages/signup/Gender';
import Age from './pages/signup/Age';
import Location from './pages/signup/Location';
import Nickname from './pages/signup/Nickname';
import Main from './pages/Main';
import InternetApply from './pages/InternetApply';
import AuthCallback from './pages/auth/AuthCallback';
import NaverCallback from './pages/auth/NaverCallback';
import KakaoCallback from './pages/auth/KakaoCallback';
import AppleCallback from './pages/auth/AppleCallback';
import PublicTerms from './pages/public/PublicTerms';
import PublicPrivacy from './pages/public/PublicPrivacy';
import { AuthProvider } from './contexts/AuthContext';
import { AlertProvider } from './contexts/AlertContext';
import './App.css';

// Flutter 브릿지를 사용하는 내부 컴포넌트
const AppContent: React.FC<{
  shouldShowPopup: boolean;
  setShouldShowPopup: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ shouldShowPopup, setShouldShowPopup }) => {
  // Flutter 브릿지 초기화 (Router 내부에서 사용해야 함)
  useFlutterBridge();

  return (
    <div className="App">
      <Routes>
        {/* 초기 화면 */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />

        {/* 공개 페이지 (비로그인 접근 가능) */}
        <Route path="/terms" element={<PublicTerms />} />
        <Route path="/privacy" element={<PublicPrivacy />} />

        {/* 회원가입 플로우 */}
        <Route path="/signup/phone" element={<PhoneInput />} />
        <Route path="/signup/phone-verification" element={<PhoneVerification />} />
        <Route path="/signup/verification" element={<Verification />} />
        <Route path="/signup/carrier" element={<Carrier />} />
        <Route path="/signup/gender" element={<Gender />} />
        <Route path="/signup/age" element={<Age />} />
        <Route path="/signup/location" element={<Location />} />
        <Route path="/signup/nickname" element={<Nickname />} />

        {/* 메인 페이지 (인증 필요) - 모든 서브 라우트 포함 */}
        <Route path="/main/*" element={
          <ProtectedRoute>
            <Main shouldShowPopup={shouldShowPopup} setShouldShowPopup={setShouldShowPopup} />
          </ProtectedRoute>
        } />

        {/* 인터넷 가입신청서 (인증 필요) */}
        <Route path="/internet-apply" element={
          <ProtectedRoute>
            <InternetApply />
          </ProtectedRoute>
        } />

        {/* OAuth 콜백 */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/callback/naver" element={<NaverCallback />} />
        <Route path="/auth/callback/kakao" element={<KakaoCallback />} />
        <Route path="/auth/callback/apple" element={<AppleCallback />} />

        {/* 기타 */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </div>
  );
};

function App() {
  // OAuth 콜백 해시가 있으면 즉시 /auth/callback으로 리다이렉트
  if (window.location.hash.includes('access_token') && window.location.pathname === '/') {
    window.location.href = '/auth/callback' + window.location.hash;
  }

  // OAuth 콜백 또는 특정 경로인 경우 Splash 건너뛰기 (한 번만 계산)
  const shouldSkipSplash = useMemo(() => {
    const isOAuthCallback = window.location.pathname.includes('/auth/callback') || window.location.hash.includes('access_token');
    const isSpecificRoute = window.location.pathname !== '/' && window.location.pathname !== '/login';
    return isOAuthCallback || isSpecificRoute;
  }, []);

  // Flutter 앱에서 접속 시 body에 클래스 추가 (하단 safe area 패딩용)
  useEffect(() => {
    const checkFlutterApp = () => {
      const flutterPlatform = localStorage.getItem('flutter_device_platform');
      if (flutterPlatform === 'ios' || flutterPlatform === 'android') {
        document.body.classList.add('flutter-app');
      }
    };

    // 초기 체크
    checkFlutterApp();

    // Flutter에서 토큰 전달 시 다시 체크
    window.addEventListener('flutterFcmToken', checkFlutterApp);
    return () => window.removeEventListener('flutterFcmToken', checkFlutterApp);
  }, []);

  const [showSplash, setShowSplash] = useState(!shouldSkipSplash);
  const [shouldShowPopup, setShouldShowPopup] = useState(false);

  // 팝업 표시 여부를 체크하는 함수
  const checkPopupVisibility = useCallback(() => {
    const popupHideUntil = localStorage.getItem('popup_hide_until');
    const now = new Date().getTime();

    if (!popupHideUntil || now > parseInt(popupHideUntil)) {
      setShouldShowPopup(true);
    }
  }, []);

  const handleSplashFinish = useCallback(() => {
    checkPopupVisibility();
    setShowSplash(false);
  }, [checkPopupVisibility]);

  // Splash를 건너뛰는 경우에도 팝업 체크
  useEffect(() => {
    if (shouldSkipSplash) {
      checkPopupVisibility();
    }
  }, [shouldSkipSplash, checkPopupVisibility]);

  if (showSplash) {
    return <Splash onFinish={handleSplashFinish} />;
  }

  return (
    <AuthProvider>
      <AlertProvider>
        <Router>
          <AppContent shouldShowPopup={shouldShowPopup} setShouldShowPopup={setShouldShowPopup} />
        </Router>
      </AlertProvider>
    </AuthProvider>
  );
}

export default App;
